import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import PropTypes from "prop-types";
import * as api from "utils/api";
import { warnUnknownPolicyDescriptions, readUserGroupsFromStorage, userSatisfiesAnyPolicyKey } from "utils/groupPolicyEngine";
import { buildSecAppAccessFromRows, readUserGroupIds } from "utils/secAppAccess";

const GroupAccessContext = createContext(null);

export function GroupAccessProvider({ children }) {
  const [secGroupsCatalog, setSecGroupsCatalog] = useState([]);
  const [restrictedSecApps, setRestrictedSecApps] = useState([]);
  const [userAllowedSecApps, setUserAllowedSecApps] = useState([]);
  const [userDeniedSecApps, setUserDeniedSecApps] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState(null);

  const refreshSecAppAccess = useCallback(async () => {
    try {
      const rows = await api.post("/db/sec_groups_apps/filter", {
        filters: {},
        columns: ["group_id", "app_name", "priv_access"],
        limit: 5000,
      });
      const list = Array.isArray(rows) ? rows : [];
      const access = buildSecAppAccessFromRows(list, readUserGroupIds());
      setRestrictedSecApps(access.restrictedSecApps);
      setUserAllowedSecApps(access.userAllowedSecApps);
      setUserDeniedSecApps(access.userDeniedSecApps);
      return access;
    } catch (err) {
      console.error("[GroupAccessProvider] sec_groups_apps access load failed:", err);
      setRestrictedSecApps([]);
      setUserAllowedSecApps([]);
      setUserDeniedSecApps([]);
      return { restrictedSecApps: [], userAllowedSecApps: [], userDeniedSecApps: [] };
    }
  }, []);

  const refreshSecGroupsCatalog = useCallback(async () => {
    setCatalogLoading(true);
    setCatalogError(null);
    try {
      const rows = await api.post("/db/sec_groups/filter", {
        filters: {},
        order_by: "group_id ASC",
        limit: 500,
      });
      const list = Array.isArray(rows) ? rows : [];
      setSecGroupsCatalog(list);
      warnUnknownPolicyDescriptions(list);
      await refreshSecAppAccess();
    } catch (err) {
      console.error("[GroupAccessProvider] sec_groups catalog failed:", err);
      setCatalogError(err?.message || "Failed to load sec_groups");
      setSecGroupsCatalog([]);
      setRestrictedSecApps([]);
      setUserAllowedSecApps([]);
      setUserDeniedSecApps([]);
    } finally {
      setCatalogLoading(false);
    }
  }, [refreshSecAppAccess]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const user = localStorage.getItem("user");
    if (token && user) refreshSecGroupsCatalog();
  }, [refreshSecGroupsCatalog]);

  const canAccessPolicy = useCallback((policyKey) => {
    const keys = Array.isArray(policyKey) ? policyKey : [policyKey];
    const filtered = keys.filter((k) => typeof k === "string" && k.trim());
    if (!filtered.length) return true;
    const userGroups = readUserGroupsFromStorage();
    return userSatisfiesAnyPolicyKey(userGroups, filtered);
  }, []);

  const value = useMemo(
    () => ({
      secGroupsCatalog,
      restrictedSecApps,
      userAllowedSecApps,
      userDeniedSecApps,
      catalogLoading,
      catalogError,
      refreshSecGroupsCatalog,
      refreshSecAppAccess,
      canAccessPolicy,
    }),
    [
      secGroupsCatalog,
      restrictedSecApps,
      userAllowedSecApps,
      userDeniedSecApps,
      catalogLoading,
      catalogError,
      refreshSecGroupsCatalog,
      refreshSecAppAccess,
      canAccessPolicy,
    ]
  );

  return <GroupAccessContext.Provider value={value}>{children}</GroupAccessContext.Provider>;
}

GroupAccessProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useGroupAccess() {
  const ctx = useContext(GroupAccessContext);
  if (!ctx) {
    throw new Error("useGroupAccess must be used within GroupAccessProvider (MainLayout wraps the app)");
  }
  return ctx;
}

/** Client portal: read-only; no staff policy checks (billing view only). */
export function ClientPortalGroupAccessProvider({ children }) {
  const canAccessPolicy = useCallback(() => false, []);
  const refreshSecGroupsCatalog = useCallback(async () => {}, []);

  const value = useMemo(
    () => ({
      secGroupsCatalog: [],
      restrictedSecApps: [],
      userAllowedSecApps: [],
      userDeniedSecApps: [],
      catalogLoading: false,
      catalogError: null,
      refreshSecGroupsCatalog,
      refreshSecAppAccess: refreshSecGroupsCatalog,
      canAccessPolicy,
    }),
    [canAccessPolicy, refreshSecGroupsCatalog]
  );

  return <GroupAccessContext.Provider value={value}>{children}</GroupAccessContext.Provider>;
}

ClientPortalGroupAccessProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
